<template>
  <article
    class="flex flex-col gap-1 rounded-md border bg-white px-2.5 py-2"
    :class="
      isSelected
        ? 'border-primary shadow-[inset_0_0_0_1px_theme(colors.primary)]'
        : 'border-lightGray30'
    "
    :data-testid="testId"
  >
    <!-- Topo do M3: countdown da etapa atual + chip do estado (Q3). -->
    <div class="flex flex-wrap items-start justify-between gap-2">
      <PrioritySlaCountdown
        :sla="sla"
        :row="row"
        :now-ms="nowMs"
        :thresholds="thresholds"
        :test-id="`${testId}-countdown`"
      />
      <PrioritySlaChip
        :sla="sla"
        :now-ms="nowMs"
        :thresholds="thresholds"
        :test-id="`${testId}-chip`"
      />
    </div>

    <div class="flex items-baseline justify-between gap-2">
      <span class="flex items-center gap-1.5 text-sm font-medium text-textPrimary">
        <input
          v-if="selectable"
          type="checkbox"
          class="h-4 w-4 cursor-pointer accent-primary"
          :aria-label="`Selecionar plantão ${row.doctorStationId}`"
          :checked="isSelected"
          @change="emit('toggle-select', row.doctorStationId)"
        />
        {{ row.doctorName }}
      </span>
      <span class="font-mono text-xs text-neutral50">{{ row.doctorStationId }}</span>
    </div>

    <!-- Frente G (Q37): horário + fuso + chips de unidade/setor/posição numa
         linha SÓ. `flex-nowrap` + `overflow-hidden`: nome longo trunca no chip
         (texto inteiro no `title`), nunca quebra linha nem aumenta o card. -->
    <div
      class="flex h-4 min-w-0 flex-nowrap items-center gap-1 overflow-hidden whitespace-nowrap text-xs text-neutral50"
      :data-testid="`${testId}-place`"
    >
      <span class="shrink-0">{{ stationTime.text }}</span>
      <!-- Sempre visível (Q18). O destaque azul é só para o fuso que NÃO é o de
           Brasília — ler "UTC-3" e "UTC-4" com a mesma cor faria o plantão de
           outro fuso passar batido. -->
      <span
        class="shrink-0 rounded px-1.5 py-0 font-mono text-[11px] leading-4"
        :class="
          stationTime.isOutsideBrasilia
            ? 'bg-blue-50 text-blue-700'
            : 'bg-lightGray30 text-neutral50'
        "
        :data-testid="`${testId}-timezone`"
      >
        {{ stationTime.timezoneLabel }}
      </span>
      <span
        v-for="chip in placeChips"
        :key="chip.key"
        class="min-w-0 max-w-[9rem] h-4 truncate rounded border border-lightGray30 px-1.5 py-0 text-[11px] leading-[14px]"
        :title="chip.title"
        :data-testid="`${testId}-place-${chip.key}`"
      >
        {{ chip.label }}
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-1">
      <span
        v-if="kindLabel"
        class="rounded border px-1.5 py-px text-[11px] font-bold"
        :class="
          sla?.kind === 'ANTECIPE' ? 'border-blue-300 text-blue-700' : 'border-primary text-primary'
        "
        :data-testid="`${testId}-sla-kind`"
      >
        {{ kindLabel }}
      </span>
      <span
        v-for="(type, index) in row.types"
        :key="`${type}-${index}`"
        class="rounded border border-current px-1.5 py-px text-[11px] font-bold"
        :class="
          type.startsWith('IN_CASH')
            ? 'text-primary'
            : type === 'ANTECIPE_PAID'
              ? 'text-red-700'
              : 'text-blue-700'
        "
        :data-testid="`${testId}-type-${index}`"
      >
        {{ PRIORITY_TYPE_LABELS[type] }}
      </span>
      <span
        class="rounded-full px-2 py-0.5 text-xs font-medium"
        :class="QUEUE_TONE_PILL_CLASS[PRIORITY_SITUATION_TONES[row.situation]]"
        :data-testid="`${testId}-situation`"
      >
        {{ prioritySituationLabel(row) }}
      </span>
      <span
        v-for="reason in criticalReasons"
        :key="reason"
        class="rounded-full border border-red-200 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700"
        :data-testid="`${testId}-critical`"
      >
        {{ reason }}
      </span>
    </div>

    <!-- Frente B §6 — etiqueta compacta de elegibilidade; motivo sempre visível (como a D2). -->
    <div v-if="eligibilityBadges.length" class="flex flex-wrap gap-1">
      <span
        v-for="badge in eligibilityBadges"
        :key="badge.modality"
        class="rounded-full px-2 py-0.5 text-[11px] font-bold"
        :class="QUEUE_TONE_PILL_CLASS[badge.tone]"
        :data-testid="`${testId}-eligibility-${badge.modality}`"
      >
        {{ badge.label }}
      </span>
    </div>
    <p
      v-for="line in eligibilityReasonLines"
      :key="line"
      class="m-0 max-w-[38ch] text-[11px] text-neutral50"
      :data-testid="`${testId}-eligibility-reason`"
    >
      {{ line }}
    </p>

    <p v-if="also" class="m-0 text-xs text-neutral50" :data-testid="`${testId}-also`">
      Também: <b class="text-textPrimary">{{ also.kindLabel }}</b> · {{ also.detail }}
    </p>

    <!-- D2 (a) — a justificativa fica SEMPRE visível. Escondê-la num tooltip
         obrigaria a Operação a abrir um por um para saber o que cobrar. -->
    <p
      v-if="row.activityRecord?.rejectionJustification"
      class="m-0 max-w-[38ch] border-l-2 border-red-600 pl-2 text-xs text-neutral50"
      :data-testid="`${testId}-justification`"
    >
      {{ row.activityRecord.rejectionJustification }}
    </p>

    <p class="m-0 text-[11px] text-neutral50" :data-testid="`${testId}-record`">
      {{ recordLine }}
    </p>

    <p
      v-if="antecipeStart"
      class="m-0 text-[11px] text-neutral50"
      :data-testid="`${testId}-antecipe-start`"
    >
      Início do SLA Antecipe: {{ antecipeStart }}
    </p>

    <p
      v-if="row.followup.claimedByName"
      class="m-0 text-[11px] text-neutral50"
      :data-testid="`${testId}-claim`"
    >
      em tratativa por {{ row.followup.claimedByName }} desde {{ claimedAtLabel }}
    </p>

    <!-- Dado pessoal: o backend só manda o telefone a quem tem SEND_RECORD.
         Sem o campo, nada é exibido — nem placeholder, nem máscara. -->
    <p
      v-if="row.doctorPhone"
      class="m-0 text-[11px] text-neutral50"
      :data-testid="`${testId}-phone`"
    >
      {{ row.doctorPhone }}
    </p>

    <div class="flex flex-wrap gap-1">
      <button
        v-for="action in actions"
        :key="action.key"
        type="button"
        class="rounded-[5px] border border-lightGray30 bg-transparent px-2 py-0.5 text-xs text-primary"
        :data-testid="action.testId"
        @click="emit('action', { key: action.key, row })"
      >
        {{ action.label }}
      </button>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { QUEUE_TONE_PILL_CLASS } from '@/components/designSystem/QueueViews/types'
import { formatHourMinuteInOffset } from '@/utils/date'
import type {
  PriorityConfig,
  PriorityPaymentRow
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { priorityRowActions } from '../actions'
import type { PriorityActionDescriptor } from '../actions'
import {
  PRIORITY_SITUATION_TONES,
  PRIORITY_TYPE_LABELS,
  prioritySituationLabel
} from '../constants'
import { priorityPlaceChips } from '../placeFormat'
import { formatPriorityStationTime } from '../rowFormat'
import {
  formatAntecipeStart,
  formatSlaAlso,
  formatSlaKindLabel,
  priorityCriticalReasons,
  priorityThresholdsFor
} from '../slaFormat'
import { formatActivityRecordCell, formatAuditCell } from '../tableColumns'
import { priorityEligibilityBadges, priorityEligibilityReasonLines } from '../eligibilityFormat'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import PrioritySlaChip from './PrioritySlaChip.vue'
import PrioritySlaCountdown from './PrioritySlaCountdown.vue'

const props = withDefaults(
  defineProps<{
    row: PriorityPaymentRow
    selectable?: boolean
    isSelected?: boolean
    /** Tique da página (`usePrioritySlaTick`); `null` = estado do servidor. */
    nowMs?: number | null
    config?: PriorityConfig | null
    testId?: string
  }>(),
  {
    selectable: false,
    isSelected: false,
    nowMs: null,
    config: null,
    testId: 'priority-payment-card'
  }
)

const emit = defineEmits<{
  'toggle-select': [doctorStationId: number]
  action: [payload: { key: PriorityActionDescriptor['key']; row: PriorityPaymentRow }]
}>()

const NOT_STARTED_RECORD_LINE: Partial<Record<PriorityPaymentRow['situation'], string>> = {
  IN_PROGRESS: 'Plantão em andamento; o registro só é cobrado depois do fim.',
  FUTURE: 'Plantão ainda não começou.'
}

const sla = computed(() => props.row.sla ?? null)
const thresholds = computed(() =>
  sla.value ? priorityThresholdsFor(props.config, sla.value.kind) : null
)
const stationTime = computed(() => formatPriorityStationTime(props.row))
const placeChips = computed(() => priorityPlaceChips(props.row))
const actions = computed(() => priorityRowActions(props.row))
const criticalReasons = computed(() => priorityCriticalReasons(props.row))

const { flags: featureFlags } = useFeatureFlags()
const eligibilityBadges = computed(() =>
  featureFlags.enabledPaymentEligibility ? priorityEligibilityBadges(props.row) : []
)
const eligibilityReasonLines = computed(() =>
  featureFlags.enabledPaymentEligibility ? priorityEligibilityReasonLines(props.row) : []
)

/** Sem rótulo de tipo no "Não iniciado" (M3): o prazo embaixo do "—" já explica. */
const kindLabel = computed(() =>
  sla.value && sla.value.state !== 'NOT_STARTED'
    ? formatSlaKindLabel(sla.value, props.row, props.nowMs)
    : null
)

const also = computed(() =>
  sla.value?.secondary
    ? formatSlaAlso(sla.value.secondary, props.row, props.nowMs, props.config)
    : null
)

const antecipeStart = computed(() =>
  sla.value && sla.value.state !== 'NOT_STARTED' ? formatAntecipeStart(props.row) : null
)

const recordLine = computed(() => {
  const upcoming = NOT_STARTED_RECORD_LINE[props.row.situation]
  if (upcoming) return upcoming
  const record = `Registro: ${formatActivityRecordCell(props.row)}`
  if (props.nowMs === null) return record
  return `${record} · Auditoria: ${formatAuditCell(props.row, new Date(props.nowMs).toISOString())}`
})

const claimedAtLabel = computed(() =>
  props.row.followup.claimedAt
    ? (formatHourMinuteInOffset(props.row.followup.claimedAt, props.row.timezone) ?? '')
    : ''
)
</script>
